import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, ChevronDown, RefreshCw, ExternalLink } from "lucide-react";
import { usePlatformAdmin } from "@/hooks/use-platform-admin";
import { usePlatformSettlements } from "@/hooks/use-platform-settlements";
import { useToast } from "@/hooks/use-toast";
import {
  formatSettlementCurrency,
  getBusinessPayoutDestination,
  groupPaymentsByBusiness,
  sumPaymentBatch,
  type PlatformPaymentSettlement,
} from "@/lib/platform-settlements";
import { PaymentSettlementTable } from "@/components/platform-settlements/PaymentSettlementTable";
import { RecordPayoutDialog } from "@/components/platform-settlements/RecordPayoutDialog";
import { format } from "date-fns";

export default function PlatformPayouts() {
  const { isSuperAdmin, loading: roleLoading } = usePlatformAdmin();
  const { toast } = useToast();
  const {
    loading,
    readyPayments,
    onHoldPayments,
    payoutHistory,
    overview,
    businesses,
    refresh,
    fetchPayoutItems,
    recordPayout,
  } = usePlatformSettlements();

  const [payoutBusyId, setPayoutBusyId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogBusinessId, setDialogBusinessId] = useState<string | null>(null);
  const [dialogPayments, setDialogPayments] = useState<PlatformPaymentSettlement[]>([]);
  const [payoutChannel, setPayoutChannel] = useState("bank");
  const [payoutReference, setPayoutReference] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [expandedBiz, setExpandedBiz] = useState<Set<string>>(new Set());
  const [historyItems, setHistoryItems] = useState<Record<string, PlatformPaymentSettlement[]>>({});
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!roleLoading && isSuperAdmin) {
      refresh();
    }
  }, [roleLoading, isSuperAdmin, refresh]);

  const readyByBusiness = useMemo(() => {
    const map = groupPaymentsByBusiness(readyPayments);
    return Array.from(map.entries()).map(([businessId, payments]) => ({
      businessId,
      payments,
      ...sumPaymentBatch(payments),
    }));
  }, [readyPayments]);

  const openPayoutDialog = (businessId: string, payments: PlatformPaymentSettlement[]) => {
    const biz = businesses[businessId];
    const destination = getBusinessPayoutDestination(biz);
    setDialogBusinessId(businessId);
    setDialogPayments(payments);
    setPayoutReference("");
    setPayoutNotes("");
    if (destination.method === "momo" || destination.method === "mpesa") {
      setPayoutChannel("momo");
    } else {
      setPayoutChannel("bank");
    }
    setDialogOpen(true);
  };

  const confirmPayout = async () => {
    if (!dialogBusinessId) return;
    setPayoutBusyId(dialogBusinessId);
    try {
      const id = await recordPayout({
        businessId: dialogBusinessId,
        reference: payoutReference.trim() || null,
        notes: payoutNotes.trim() || null,
        channel: payoutChannel,
      });
      toast({
        title: "Payout recorded",
        description: `Batch ${String(id).slice(0, 8)}… marked paid out.`,
      });
      setDialogOpen(false);
      await refresh();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Try again.";
      toast({ title: "Payout failed", description: msg, variant: "destructive" });
    } finally {
      setPayoutBusyId(null);
    }
  };

  const toggleBizExpand = (businessId: string) => {
    setExpandedBiz((prev) => {
      const next = new Set(prev);
      if (next.has(businessId)) next.delete(businessId);
      else next.add(businessId);
      return next;
    });
  };

  const loadHistoryDetail = async (payoutId: string) => {
    if (historyItems[payoutId]) return;
    setHistoryLoading(payoutId);
    try {
      const items = await fetchPayoutItems(payoutId);
      setHistoryItems((prev) => ({ ...prev, [payoutId]: items }));
    } catch {
      toast({ title: "Could not load payout line items", variant: "destructive" });
    } finally {
      setHistoryLoading(null);
    }
  };

  if (roleLoading || !isSuperAdmin) {
    return (
      <AppShell businessName="Buukd Platform">
        <PageHeader title="Settlements" description="Superadmin only" />
        <div className="p-6">
          {!roleLoading && !isSuperAdmin ? (
            <p className="text-sm text-muted-foreground">You don’t have access to this page.</p>
          ) : (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          )}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell businessName="Buukd Platform">
      <PageHeader
        title="Settlements"
        description="Pay businesses when Paystack confirms — Buukd keeps the booking fee"
        action={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refresh()} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/app/super-admin/payments">
                All payments
                <ExternalLink className="h-3 w-3 ml-2" />
              </Link>
            </Button>
          </div>
        }
      />

      <div className="p-4 sm:p-6 space-y-4">
        {loading && !overview ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Ready to pay</CardTitle>
                <CardDescription>Net owed (Buukd Paystack)</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatSettlementCurrency(overview?.readyToPayNet ?? 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {overview?.readyPaymentCount ?? 0} payment(s)
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Buukd revenue</CardTitle>
                <CardDescription>Booking fees + signup</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatSettlementCurrency(overview?.platformRevenue ?? 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Paid out</CardTitle>
                <CardDescription>Sent to businesses (all time)</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatSettlementCurrency(overview?.paidOutNet ?? 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">On hold</CardTitle>
                <CardDescription>Confirmed, not yet payable</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatSettlementCurrency(overview?.onHoldNet ?? 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">{overview?.onHoldCount ?? 0} payment(s)</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="ready" className="space-y-4">
          <TabsList>
            <TabsTrigger value="ready">
              Ready to pay
              {readyByBusiness.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                  {readyByBusiness.length}
                </Badge>
              )}
            </TabsTrigger>
            {onHoldPayments.length > 0 && (
              <TabsTrigger value="hold">
                On hold
                <Badge variant="outline" className="ml-2 h-5 px-1.5">
                  {onHoldPayments.length}
                </Badge>
              </TabsTrigger>
            )}
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="ready" className="space-y-4 mt-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : readyByBusiness.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-sm text-muted-foreground">
                  Nothing ready to pay. New online payments appear here right after Paystack confirms.
                </CardContent>
              </Card>
            ) : (
              readyByBusiness.map((g) => {
                const biz = businesses[g.businessId];
                const name = biz?.name || "Business";
                const destination = getBusinessPayoutDestination(biz);
                const expanded = expandedBiz.has(g.businessId);
                return (
                  <Card key={g.businessId}>
                    <CardContent className="p-4 sm:p-6 space-y-4">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-lg">{name}</h3>
                          <p className="text-xs text-muted-foreground">{g.count} payments</p>
                        </div>
                        <div className="text-right text-sm space-y-1">
                          <div>
                            <span className="text-muted-foreground">Pay business </span>
                            <span className="font-bold text-lg">
                              {formatSettlementCurrency(g.net, g.currency)}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Buukd keeps {formatSettlementCurrency(g.platformFee, g.currency)}
                          </div>
                        </div>
                      </div>

                      <div
                        className={`rounded-lg border p-3 text-sm space-y-1 ${
                          destination.connected && destination.accountDetail
                            ? "border-border bg-muted/40"
                            : "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-50"
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Payout details
                          </p>
                          <Badge variant="secondary" className="text-[11px]">
                            {destination.methodLabel}
                          </Badge>
                          {destination.connected && destination.accountDetail ? (
                            <Badge className="bg-success/10 text-success border-success/20 text-[11px]">
                              Connected
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[11px]">
                              Missing details
                            </Badge>
                          )}
                        </div>
                        {destination.connected && destination.accountDetail ? (
                          <>
                            {destination.providerLabel ? (
                              <p className="font-medium text-foreground">{destination.providerLabel}</p>
                            ) : null}
                            <p className="font-mono text-base text-foreground">{destination.accountDetail}</p>
                            <p className="text-xs text-muted-foreground">
                              Send {formatSettlementCurrency(g.net, g.currency)} via {destination.methodLabel.toLowerCase()}, then record the payout.
                            </p>
                          </>
                        ) : (
                          <p className="text-sm">
                            This business has not connected a MoMo or bank account in Settings → Payments & Payouts.
                          </p>
                        )}
                      </div>

                      <Collapsible open={expanded} onOpenChange={() => toggleBizExpand(g.businessId)}>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="w-full justify-between">
                            View line items
                            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-3">
                          <PaymentSettlementTable payments={g.payments} />
                        </CollapsibleContent>
                      </Collapsible>

                      <div className="flex justify-end">
                        <Button
                          onClick={() => openPayoutDialog(g.businessId, g.payments)}
                          disabled={payoutBusyId === g.businessId}
                        >
                          Record payout
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="hold">
            <Card className="mb-4 border-dashed">
              <CardContent className="p-4 text-sm text-muted-foreground">
                Confirmed on Buukd Paystack only. Abandoned checkouts and merchants on their own Paystack are
                excluded. Use &quot;All payments&quot; for the full ledger.
              </CardContent>
            </Card>
            {onHoldPayments.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-sm text-muted-foreground">Nothing on hold.</CardContent>
              </Card>
            ) : (
              <PaymentSettlementTable payments={onHoldPayments} />
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-3">
            {payoutHistory.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-sm text-muted-foreground">No payout batches yet.</CardContent>
              </Card>
            ) : (
              payoutHistory.map((batch) => {
                const bizName =
                  batch.business?.name || businesses[batch.business_id]?.name || "Business";
                const expanded = historyItems[batch.id] !== undefined;
                return (
                  <Card key={batch.id}>
                    <CardHeader className="pb-2">
                      <div className="flex flex-wrap justify-between gap-2">
                        <div>
                          <CardTitle className="text-base">{bizName}</CardTitle>
                          <CardDescription>
                            {format(new Date(batch.processed_at), "MMM d, yyyy 'at' h:mm a")}
                            {batch.payout_reference && ` · Ref ${batch.payout_reference}`}
                            {batch.payout_channel && ` · ${batch.payout_channel}`}
                          </CardDescription>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">
                            {formatSettlementCurrency(Number(batch.total_net_amount), batch.currency)}
                          </p>
                          <p className="text-xs text-muted-foreground">net to business</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (!expanded) loadHistoryDetail(batch.id);
                          else
                            setHistoryItems((prev) => {
                              const next = { ...prev };
                              delete next[batch.id];
                              return next;
                            });
                        }}
                      >
                        {historyLoading === batch.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : expanded ? (
                          "Hide payments"
                        ) : (
                          "Show payments"
                        )}
                      </Button>
                      {historyItems[batch.id] && (
                        <div className="mt-3">
                          <PaymentSettlementTable payments={historyItems[batch.id]} compact />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>

      <RecordPayoutDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        businessName={
          dialogBusinessId ? businesses[dialogBusinessId]?.name || "Business" : "Business"
        }
        business={dialogBusinessId ? businesses[dialogBusinessId] : null}
        payments={dialogPayments}
        channel={payoutChannel}
        onChannelChange={setPayoutChannel}
        reference={payoutReference}
        onReferenceChange={setPayoutReference}
        notes={payoutNotes}
        onNotesChange={setPayoutNotes}
        busy={payoutBusyId === dialogBusinessId}
        onConfirm={confirmPayout}
      />
    </AppShell>
  );
}
